import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import APIClient from "@/APIClient";
import { QueryKey } from "./query-keys";
import type { MetadataDetectorPayload, PaginationParams } from "@/types";
import type { Ref } from "vue";

export const useGetAllMetadataDetectors = (
  pagination: Ref<PaginationParams>,
  filters: Ref<{
    isDefault?: boolean;
  }>,
) =>
  useQuery({
    queryKey: [QueryKey.METADATA_DETECTORS, pagination, filters],
    queryFn: async () => {
      return await APIClient.fetchAllMetadataDetectors({
        ...filters.value,
        ...pagination.value,
      });
    },
  });

export const useUpdateMetadataDetector = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...payload
    }: {
      id: number;
    } & Partial<MetadataDetectorPayload>) => {
      return await APIClient.updateMetadataDetector(id, payload);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.METADATA_DETECTORS],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORT_TEMPLATE, data?.reportTemplateId],
      });
    },
  });
};

export const useCreateMetadataDetector = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: MetadataDetectorPayload) => {
      return await APIClient.createMetadataDetector(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.METADATA_DETECTORS],
      });
    },
  });
};

export const useDeleteMetadataDetector = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await APIClient.deleteMetadataDetector(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.METADATA_DETECTORS],
      });
    },
  });
};
